// Past-due subscriptions keep full access while FlyCode retries the failed
// renewal; the banner only warns. Profiles never store past_due (klap-api
// persists active/paused/canceled only, and every access check reads
// "active"), so the banner reads the Stripe status that GET
// /billing/subscription returns instead.

export type BillingSubscription = {
  status?: string;
  current_period_start?: number | null;
  current_period_end?: number | null;
};

// FlyCode cancels a subscription whose renewal is still unpaid about a month
// after the failure: Sep 2026 cancellations came 29.5 days after the failed
// invoice (p50; p90 29.8). A monthly plan loses access around its period
// end, a yearly plan a month after the failed renewal, long before its
// period ends.
export const PAST_DUE_ACCESS_DAYS = 29;

const DAY_MS = 24 * 60 * 60 * 1000;

export const findPastDueSubscription = <T extends BillingSubscription>(
  subscriptions?: T[] | null,
): T | undefined =>
  subscriptions?.find((subscription) => subscription.status === "past_due");

// When a past-due subscription loses access: the earlier of its period end
// and PAST_DUE_ACCESS_DAYS after the failed renewal (its period start). Null
// when unknown or already passed, so the banner never shows a past date.
export const pastDueAccessEndsAt = (
  subscription: BillingSubscription,
  now: number = Date.now(),
): Date | null => {
  const candidates = [
    subscription.current_period_end
      ? subscription.current_period_end * 1000
      : null,
    subscription.current_period_start
      ? subscription.current_period_start * 1000 + PAST_DUE_ACCESS_DAYS * DAY_MS
      : null,
  ].filter((time): time is number => time !== null);
  if (candidates.length === 0) return null;
  const endsAt = Math.min(...candidates);
  return endsAt > now ? new Date(endsAt) : null;
};
